// Componente para sugerir instalação do PWA (Android nativo + guia iOS)
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, X, Smartphone, Share, PlusSquare, ArrowDown, Copy } from 'lucide-react';
import { toast } from 'sonner';

const IOSGuide = ({ isIPad, inAppBrowser }) => {
  if (inAppBrowser) {
    return (
      <div className="bg-white/15 rounded-xl p-3 text-sm text-white space-y-2" data-testid="pwa-ios-inapp-guide">
        <p>Para instalar, abra este site no <strong>Safari</strong>.</p>
        <Button
          size="sm"
          className="bg-white text-emerald-700 hover:bg-emerald-50 w-full"
          onClick={() => {
            navigator.clipboard?.writeText(window.location.origin).then(
              () => toast.success('Link copiado!', { description: 'Cole no Safari para instalar o app.' }),
              () => {}
            );
          }}
          data-testid="pwa-ios-copy-link-btn"
        >
          <Copy className="w-4 h-4 mr-1" /> Copiar link
        </Button>
      </div>
    );
  }
  const steps = [
    { icon: Share, text: <>Toque no botão <strong>Compartilhar</strong> {isIPad ? 'no topo da tela' : 'na barra inferior do Safari'}</> },
    { icon: PlusSquare, text: <>Role e toque em <strong>"Adicionar à Tela de Início"</strong></> },
    { icon: Download, text: <>Toque em <strong>"Adicionar"</strong> no canto superior direito</> },
  ];
  return (
    <ol className="bg-white/15 rounded-xl p-3 space-y-2" data-testid="pwa-ios-guide">
      {steps.map(({ icon: Icon, text }, i) => (
        <li key={i} className="flex items-center gap-2 text-sm text-white">
          <span className="w-6 h-6 rounded-full bg-white text-emerald-700 font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</span>
          <Icon className="w-4 h-4 shrink-0" />
          <span>{text}</span>
        </li>
      ))}
    </ol>
  );
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isIPad, setIsIPad] = useState(false);
  const [inAppBrowser, setInAppBrowser] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    const ua = navigator.userAgent;
    const iPad = /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const iOS = (/iPhone|iPod/.test(ua) || iPad) && !window.MSStream;
    setIsIOS(iOS);
    setIsIPad(iPad);
    setInAppBrowser(/FBAN|FBAV|Instagram|Line\/|TikTok|Twitter/i.test(ua));

    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const daysSinceDismissed = (Date.now() - (dismissed ? parseInt(dismissed) : 0)) / (1000 * 60 * 60 * 24);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (daysSinceDismissed > 7) setTimeout(() => setShowPrompt(true), 5000);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    if (iOS && !standalone && daysSinceDismissed > 7) {
      setTimeout(() => setShowPrompt(true), 5000);
    }
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowGuide(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (isStandalone || !showPrompt) return null;

  const canInstall = isIOS || deferredPrompt;

  return (
    <>
      <div className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96 animate-in slide-in-from-bottom duration-300" data-testid="pwa-install-prompt">
        <Card className="bg-gradient-to-r from-emerald-600 to-green-600 border-0 shadow-2xl">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                <Smartphone className="w-6 h-6 text-white" />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-white mb-1">Instale o App!</h3>
                <p className="text-sm text-emerald-100 mb-3">
                  Tenha acesso rápido ao ranking direto do seu celular!
                </p>

                {showGuide && <div className="mb-3"><IOSGuide isIPad={isIPad} inAppBrowser={inAppBrowser} /></div>}

                <div className="flex gap-2">
                  {canInstall && !showGuide && (
                    <Button
                      onClick={handleInstall}
                      size="sm"
                      className="bg-white text-emerald-600 hover:bg-emerald-50"
                      data-testid={isIOS ? 'pwa-install-ios-btn' : 'pwa-install-btn'}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Instalar App
                    </Button>
                  )}
                  <Button
                    onClick={handleDismiss}
                    size="sm"
                    variant="ghost"
                    className="text-white hover:bg-white/20"
                    data-testid="pwa-dismiss-btn"
                  >
                    {showGuide ? 'Entendi' : 'Agora não'}
                  </Button>
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleDismiss}
                className="text-white/70 hover:text-white hover:bg-white/20 shrink-0"
                data-testid="pwa-close-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {showGuide && isIOS && !isIPad && !inAppBrowser && (
        <div className="fixed bottom-2 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center pointer-events-none" data-testid="pwa-ios-arrow">
          <span className="text-xs font-semibold text-white bg-emerald-600 rounded-full px-3 py-1 shadow-lg mb-1">Toque em Compartilhar</span>
          <ArrowDown className="w-8 h-8 text-emerald-600 animate-bounce drop-shadow-lg" />
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
